export interface NormalizedErrorDetail {
  message: string;
  code?: string | number | null;
}

function normalizeError(error: unknown): NormalizedErrorDetail {
  // Axios errors often carry response data
  const isAxiosError = !!(error && (error as any).isAxiosError);
  const code =
    (isAxiosError && (error as any).response?.data?.code) ||
    (error as any)?.code ||
    null;

  const message = ((): string => {
    if (isAxiosError) {
      const respMsg = (error as any).response?.data?.message;
      return String(respMsg || (error as any).message || 'Error');
    }
    if (typeof error === 'string') return error;
    if (error && typeof (error as any).message === 'string')
      return (error as any).message;
    try {
      return String(error ?? 'Error');
    } catch {
      return 'Error';
    }
  })();

  return { message, code };
}

function isOnPatientDetailsRoute(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.location !== 'undefined' &&
      String(window.location.pathname).includes('/patientDetails')
    );
  } catch {
    return false;
  }
}

function isNoAccessPatient(detail: NormalizedErrorDetail): boolean {
  const { code, message } = detail;
  if (code === 'NO_ACCESS_PATIENT') return true;
  const msg = (message || '').toLowerCase();
  return msg.includes('no access') && msg.includes('patient');
}

function isNoAccessTenancy(detail: NormalizedErrorDetail): boolean {
  const { message } = detail;
  const msg = (message || '').toLowerCase();
  return ['User does not belong to tenancy'].some((item) => msg.includes(item));
}

function isUserNoPermission(detail: NormalizedErrorDetail): boolean {
  const { message } = detail;
  const msg = (message || '').toLowerCase();
  return ['user missing permissions', 'user has no permissions'].some((item) =>
    msg.includes(item),
  );
}

export function dispatchErrorEvent(error: unknown): void {
  const detail = normalizeError(error);

  // on patientDetails, treat error as no-access-patient
  if (isOnPatientDetailsRoute() || isNoAccessPatient(detail)) {
    window.dispatchEvent(
      new CustomEvent('global-no-access-patient', { detail }),
    );
    return;
  }

  //  Any other error related to
  //  "User has no permissions": 403,
  //  "User missing permissions": 403,
  //  "User does not belong to tenancy": 403
  if (isNoAccessTenancy(detail) || isUserNoPermission(detail)) {
    window.dispatchEvent(new CustomEvent('global-no-access-user', { detail }));
    return;
  }

  window.dispatchEvent(new CustomEvent('global-app-error', { detail }));
}

export default dispatchErrorEvent;
